import type { ProxyProtocol } from "@/api/custom_types";
import { ProxyProtocolSchema } from "@/api/enums";
import { useTranslation } from "@/hooks/useTranslation";
import { useBatchUpdateProxies } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	SmartSelect,
} from "@st-marketplace/ui";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const batchUpdateSchema = z.object({
	ip_address: z.string().nullable().optional(),
	port: z.number().min(0).max(65535).nullable().optional(),
	username: z.string().nullable(),
	password: z.string().nullable(),
	protocol: z.string().nullable(),
});
export type ProxiesBatchUpdateFormData = z.infer<typeof batchUpdateSchema>;

export interface ProxiesBatchUpdateDialogProps {
	open: boolean;
	onClose: () => void;
}

const ProxiesBatchUpdateDialog: FC<ProxiesBatchUpdateDialogProps> = ({
	open,
	onClose,
}) => {
	const { t } = useTranslation();
	const batchUpdateMutation = useBatchUpdateProxies();

	const form = useForm<ProxiesBatchUpdateFormData>({
		resolver: zodResolver(batchUpdateSchema),
		defaultValues: {
			ip_address: null,
			port: null,
			username: null,
			password: null,
			protocol: null,
		},
	});

	const protocolItems = ProxyProtocolSchema.anyOf.map((item) => ({
		value: item.const,
		label: t(`proxy.protocolOptions.${item.const}`),
	}));

	const handleFormSubmit = async (data: ProxiesBatchUpdateFormData) => {
		batchUpdateMutation.mutate(
			{
				body: {
					ip_address: null,
					username: data.username ? data.username : null,
					password: data.password ? data.password : null,
					port: data.port,
					protocol: data.protocol as ProxyProtocol,
				},
			},
			{
				onSuccess: () => {
					form.reset();
					onClose();
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="w-full max-w-sm">
				<DialogHeader>
					<DialogTitle>{t("proxy.batchUpdateDialog.title")}</DialogTitle>
					<DialogDescription className="text-start">
						{t("proxy.batchUpdateDialog.description")}
					</DialogDescription>
				</DialogHeader>
				<DialogScrollableArea>
					<Form {...form}>
						<form
							noValidate
							onSubmit={form.handleSubmit(handleFormSubmit)}
							className="flex flex-col gap-4"
							id="proxy-batch-update-form"
						>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("proxy.batchUpdateDialog.username")}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												value={field.value ?? ""}
												ltr
												placeholder={t("proxy.batchUpdateDialog.username")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("proxy.batchUpdateDialog.password")}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												value={field.value ?? ""}
												ltr
												placeholder={t("proxy.batchUpdateDialog.password")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="port"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t("proxy.batchUpdateDialog.port")}</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder={t("proxy.batchUpdateDialog.port")}
												value={field.value ?? ""}
												onChange={(e) => {
													const value = e.target.value
														? Number(e.target.value)
														: null;
													form.setValue("port", value, {
														shouldValidate: true,
													});
												}}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="protocol"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t("proxy.batchUpdateDialog.protocol")}
										</FormLabel>
										<FormControl>
											<SmartSelect
												items={protocolItems}
												value={field.value ?? undefined}
												onChange={field.onChange}
												placeholder={t("proxy.batchUpdateDialog.protocol")}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</form>
					</Form>
				</DialogScrollableArea>
				<DialogFooter
					buttons={{
						primary: {
							label: t("proxy.batchUpdateDialog.buttonUpdate"),
							props: {
								type: "submit",
								form: "proxy-batch-update-form",
								loading: batchUpdateMutation.isPending,
							},
						},
					}}
				/>
			</DialogContent>
		</Dialog>
	);
};

export default ProxiesBatchUpdateDialog;
